import { Navigate, Route, Routes } from 'react-router-dom';
import { useSession } from '../lib/session.js';
import { AppShell } from './AppShell.js';
import { LoginPage } from '../pages/LoginPage.js';
import { OverviewPage } from '../pages/OverviewPage.js';
import { PackagesPage } from '../pages/PackagesPage.js';
import { RegisterPackagePage } from '../pages/RegisterPackagePage.js';
import { ScanPage } from '../pages/ScanPage.js';
import { ConciergesPage } from '../pages/ConciergesPage.js';
import { LogsPage } from '../pages/LogsPage.js';
import { ResidentsPage } from '../pages/ResidentsPage.js';
import { StoragePage } from '../pages/StoragePage.js';
import { UnitsPage } from '../pages/UnitsPage.js';
import { PlanPage } from '../pages/PlanPage.js';
import { PickupPage } from '../pages/PickupPage.js';
import { ResetPasswordPage } from '../pages/ResetPasswordPage.js';
import { ChangePasswordPage } from '../pages/ChangePasswordPage.js';
import { SuspendedPage } from '../pages/SuspendedPage.js';
import { AdminHomePage } from '../pages/admin/AdminHomePage.js';
import { AdminSettingsPage } from '../pages/admin/AdminSettingsPage.js';
import { AdminShell } from '../pages/admin/AdminShell.js';
import { PlanChangeRequestsPage } from '../pages/admin/PlanChangeRequestsPage.js';
import { SubscribersPage } from '../pages/admin/SubscribersPage.js';
import { PlansPage } from '../pages/admin/PlansPage.js';
import { AuditPage } from '../pages/admin/AuditPage.js';
import { DispatchesPage } from '../pages/admin/DispatchesPage.js';
import { UsagePage } from '../pages/admin/UsagePage.js';
import { CondominiumPage } from '../pages/admin/CondominiumPage.js';
import { AccountsPage } from '../pages/admin/AccountsPage.js';
import { RolesPage } from '../pages/admin/RolesPage.js';

/**
 * Sao DUAS aplicacoes na mesma origem, e a divisao acontece aqui, antes de
 * qualquer decisao de sessao:
 *
 * - `/retirada/:token` — o morador, que nao tem conta. E o destino do link do
 *   WhatsApp (`PICKUP_BASE_URL`), entao ele precisa abrir para quem chega sem
 *   login. Ficando abaixo do `if (!isAuthenticated)`, como estava, todo aviso
 *   de encomenda desembocava na tela de login de um app que o morador nao usa.
 * - todo o resto — o condominio ou o backoffice, atras de sessao.
 */
export function App() {
  return (
    <Routes>
      <Route path="/retirada/:token" element={<PickupPage />} />
      {/* Mesma razao da rota acima: quem abre o link de recuperacao nao tem
          sessao, e nao ter e o proprio motivo de ele existir. */}
      <Route path="/redefinir/:token" element={<ResetPasswordPage />} />
      <Route path="*" element={<AuthenticatedApp />} />
    </Routes>
  );
}

function AuthenticatedApp() {
  const { isAuthenticated, isLoading, staff, allows, suspendedMessage, user } = useSession();

  if (isLoading) {
    return (
      <div className="safe-area-all safe-screen grid place-items-center text-muted">Carregando...</div>
    );
  }

  // A conta existe, a senha estava certa, e mesmo assim nao ha app para
  // mostrar: a Azko cortou o acesso. Sem esta tela, o login bem-sucedido
  // devolvia a propria tela de login — e o sindico concluia que tinha errado a
  // senha, em vez de ligar para resolver a pendencia.
  if (suspendedMessage) return <SuspendedPage message={suspendedMessage} />;

  if (!isAuthenticated) return <LoginPage />;

  // Antes de qualquer rota: a senha provisoria foi criada por outra pessoa, e
  // o backend recusa tudo enquanto ela valer. Mostrar o menu aqui seria
  // oferecer botoes que respondem 403.
  if (user?.mustChangePassword) return <ChangePasswordPage email={user.email} />;

  // Equipe Azko nao tem condominio, entao nao passa pelo AppShell — que existe
  // para operar UM tenant (menu por modulo contratado, medidor de plano, troca
  // de contexto). Sao dois produtos diferentes atras do mesmo login, e por isso
  // cada um tem o proprio conjunto de rotas.
  if (staff) {
    return (
      <AdminShell>
        <Routes>
          <Route path="/" element={<AdminHomePage />} />
          <Route path="/assinantes" element={<SubscribersPage />} />
          <Route path="/solicitacoes" element={<PlanChangeRequestsPage />} />
          <Route path="/planos" element={<PlansPage />} />
          <Route path="/logs" element={<AuditPage />} />
          <Route path="/disparos" element={<DispatchesPage />} />
          <Route path="/uso" element={<UsagePage />} />
          <Route path="/condominios/:id" element={<CondominiumPage />} />
          <Route path="/contas" element={<AccountsPage />} />
          <Route path="/papeis" element={<RolesPage />} />
          {/* Sem guard de permissao: e o proprio perfil de quem esta logado. */}
          <Route path="/configuracoes" element={<AdminSettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AdminShell>
    );
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<OverviewPage />} />
        <Route path="/encomendas" element={<PackagesPage />} />
        <Route path="/registrar" element={<RegisterPackagePage />} />
        <Route path="/ler-qr" element={<ScanPage />} />
        <Route path="/moradores" element={<ResidentsPage />} />
        {/* Porteiro nao gerencia porteiro: sem o guard, quem digitasse a URL
            veria a tela montar e so entao tomar 403 em cada botao. */}
        <Route
          path="/porteiros"
          element={
            allows('condominio:porteiros:visualizar') ? <ConciergesPage /> : <Navigate to="/" replace />
          }
        />
        <Route
          path="/historico"
          element={
            allows('condominio:logs:visualizar') ? <LogsPage /> : <Navigate to="/" replace />
          }
        />
        <Route
          path="/unidades"
          element={
            allows('condominio:unidades:criar') ? <UnitsPage /> : <Navigate to="/" replace />
          }
        />
        <Route
          path="/armarios"
          element={
            allows('condominio:unidades:criar') ? <StoragePage /> : <Navigate to="/" replace />
          }
        />
        {/* A API recusa /billing sem esta permissao; sem o guard aqui, quem
            digitasse /plano na barra veria um erro cru em vez do app. */}
        <Route
          path="/plano"
          element={
            allows('condominio:assinatura:visualizar') ? <PlanPage /> : <Navigate to="/" replace />
          }
        />
        {/* Rota desconhecida volta para a visao geral em vez de tela branca. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
